import React, { useState, useEffect } from "react";
import {
  FaEnvelope,
  FaGlobe,
  FaUser,
  FaEye,
  FaUpload,
  FaSignOutAlt,
} from "react-icons/fa";
import { useSelector } from "react-redux";
import CustomInput from "../common/CustomInput";
import ImageUploadModal from "./ImageUploadModal";
import ProfileCard from "./ProfileCard";
import AsyncDropdown from "../common/AsyncDropdown";
import { tp_small } from "../../constants/icons";
import { ROLES } from "../../config/roles";
import { searchService, authService, agentService } from "../../services/api";
import PhoneNumberField from "./common/PhoneNumberField";
import MobileNumberUpdateModal from "../common/MobileNumberUpdateModal";
import ChangePasswordModal from "../common/ChangePasswordModal";
import {
  LICENSE_FILE_ACCEPT,
  pickValidLicenseFile,
} from "../../utils/licenseFile";
import toast from "react-hot-toast";

const AgentAccountInfo = ({
  agentProfileData,
  formApiData,
  isEditing,
  isLoading,
  isSaving,
  error,
  onApiInputChange,
  onDocumentChange,
  onSave,
  onCancel,
  onEdit,
  onAgencySearch,
  onMobileUpdate,
}) => {
  const deriveFullName = (profileData) => {
    if (!profileData) return "";
    if (profileData.name) return profileData.name;
    const parts = [profileData.first_name, profileData.last_name].filter(Boolean);
    return parts.join(" ").trim();
  };

  const deriveAgencyName = (profileData) =>
    profileData?.agency_details?.name || profileData?.agency_name || "";

  const getInitials = (value) => {
    if (!value) return "?";
    const tokens = value.trim().split(/\s+/);
    if (tokens.length === 1) return tokens[0].charAt(0).toUpperCase();
    return (tokens[0].charAt(0) + tokens[tokens.length - 1].charAt(0)).toUpperCase();
  };

  // Function to get default form data from API data
  const getDefaultFormData = (profileData) => ({
    // Admin Details
    adminName: deriveFullName(profileData),
    licenseNumber: profileData?.license_number || "",

    // Agency Details
    agencyName: deriveAgencyName(profileData),
    emailAddress: profileData?.email || "",
    phoneNumber: profileData?.mobile || "",
    agencyWebsite: profileData?.agency_details?.website || "",
  });

  const [formData, setFormData] = useState(
    getDefaultFormData(agentProfileData)
  );

  const [showAdminImageUpload, setShowAdminImageUpload] = useState(false);
  const [showAgencyLogoUpload, setShowAgencyLogoUpload] = useState(false);
  const [showFindAgencyModal, setShowFindAgencyModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [showLicenseUpdateModal, setShowLicenseUpdateModal] = useState(false);
  const [isUpdatingLicense, setIsUpdatingLicense] = useState(false);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const handleMobileUpdateSuccess = (newMobile) => {
    handleInputChange("phoneNumber", newMobile);
    if (typeof onMobileUpdate === "function") {
      onMobileUpdate(newMobile);
    }
  };
  const [showLeaveAgencyModal, setShowLeaveAgencyModal] = useState(false);
  const [isLeavingAgency, setIsLeavingAgency] = useState(false);

  // Get current user role from Redux
  const { currentRole } = useSelector((state) => state.auth);
  const isAgent = currentRole === ROLES.Agent;

  const [adminProfileImage, setAdminProfileImage] = useState(
    agentProfileData?.profile_picture_url ||
      agentProfileData?.profile_image ||
      null
  );
  const [agencyLogo, setAgencyLogo] = useState(
    agentProfileData?.agency_logo ||
      agentProfileData?.agency_details?.profile_picture_url ||
      null
  );

  const [documentFile, setDocumentFile] = useState(null);

  // Update profile data when API data changes
  useEffect(() => {
    if (agentProfileData) {
      // Update local form data based on API data using helper function
      setFormData(getDefaultFormData(agentProfileData));

      // Update image states
      setAdminProfileImage(
        agentProfileData.profile_picture_url ||
          agentProfileData.profile_image ||
          null
      );
      setAgencyLogo(
        agentProfileData.agency_logo ||
          agentProfileData.agency_details?.profile_picture_url ||
          null
      );

      // Set document URL if available
      if (agentProfileData.document_url) {
        setDocumentFile(agentProfileData.document_url);
      }
    }
  }, [agentProfileData]);

  // Custom cancel handler that reverts all local state
  const handleCancel = () => {
    // Revert form data to original API data
    if (agentProfileData) {
      setFormData(getDefaultFormData(agentProfileData));

      // Revert image states
      setAdminProfileImage(
        agentProfileData.profile_picture_url ||
          agentProfileData.profile_image ||
          null
      );
      setAgencyLogo(
        agentProfileData.agency_logo ||
          agentProfileData.agency_details?.profile_picture_url ||
          null
      );

      // Revert document state
      setDocumentFile(agentProfileData.document_url || null);
    }

    // Call parent cancel handler
    onCancel();
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Map local form fields to API fields
    const apiFieldMap = {
      adminName: "name",
      licenseNumber: "license_number",
      agencyName: "agency_name",
      emailAddress: "email",
      phoneNumber: "mobile",
      agencyWebsite: "website",
    };

    if (apiFieldMap[field] && isEditing) {
      onApiInputChange(apiFieldMap[field], value);
    }
  };

  const handleAdminImageUpload = () => {
    setShowAdminImageUpload(true);
  };

  const handleAdminImageRemove = () => {
    setAdminProfileImage(null);
  };

  const handleAgencyLogoUpload = () => {
    setShowAgencyLogoUpload(true);
  };

  const handleAgencyLogoRemove = () => {
    setAgencyLogo(null);
  };

  const handleAdminImageUploadComplete = (croppedImage) => {
    setAdminProfileImage(croppedImage);
    setShowAdminImageUpload(false);

    console.log("croppedImage", croppedImage);
    // Convert base64 to File object if needed
    if (
      typeof croppedImage === "string" &&
      (croppedImage.startsWith("blob:") || croppedImage.startsWith("data:"))
    ) {
      fetch(croppedImage)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], "profile_image.png", {
            type: "image/png",
          });
          onApiInputChange("profile_image", file);
        });
    }
  };

  const handleAgencyLogoUploadComplete = (croppedImage) => {
    setAgencyLogo(croppedImage);
    setShowAgencyLogoUpload(false);

    console.log("croppedImage", croppedImage);
    // Convert base64 to File object if needed
    if (
      typeof croppedImage === "string" &&
      (croppedImage.startsWith("blob:") || croppedImage.startsWith("data:"))
    ) {
      fetch(croppedImage)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], "agency_logo.png", {
            type: "image/png",
          });
          onApiInputChange("agency_logo", file);
        });
    }
  };

  const handleDocumentUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setDocumentFile(file.name);
      onDocumentChange(file);
    }
  };

  const handleViewLicenseDocument = () => {
    if (agentProfileData?.document_url) {
      window.open(agentProfileData.document_url, "_blank");
    }
  };

  const handleUpdateLicense = () => {
    setShowLicenseUpdateModal(true);
  };

  const handleLicenseUpdateSubmit = async (licenseData) => {
    setIsUpdatingLicense(true);
    try {
      const response = await authService.updateAgentLicense(licenseData);
      if (response.success) {
        toast.success("License updated successfully");
        setShowLicenseUpdateModal(false);
        // Refresh the profile data
        window.location.reload();
      } else {
        toast.error(response.error || "Failed to update license");
      }
    } catch (error) {
      console.error("Error updating license:", error);
      toast.error("Failed to update license");
    } finally {
      setIsUpdatingLicense(false);
    }
  };

  const handleOpenLeaveAgency = () => {
    setShowLeaveAgencyModal(true);
  };

  const handleConfirmLeaveAgency = async () => {
    setIsLeavingAgency(true);
    try {
      const res = await agentService.leaveAgency();
      if (res.success) {
        toast.success(res.message || "Left agency successfully");
        setShowLeaveAgencyModal(false);
        // Refresh profile to reflect state
        window.location.reload();
      } else {
        toast.error(res.error || "Failed to leave agency");
      }
    } catch (e) {
      toast.error("Failed to leave agency");
    } finally {
      setIsLeavingAgency(false);
    }
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <span className="text-gray-500 text-lg">Loading profile...</span>
      </div>
    );
  }

  const adminInitials = getInitials(
    formData.adminName || deriveFullName(agentProfileData)
  );
  const agencyInitials = getInitials(
    formData.agencyName || deriveAgencyName(agentProfileData)
  );

  return (
    <>
      <div className="px-4 md:px-14 py-6 space-y-8">
        {/* Admin Details Section */}
        <div className="bg-white p-6 border border-[#E6E6E6]">
          <div className="flex justify-between items-center gap-3 mb-6 flex-col sm:flex-row">
            <h2 className="text-[11px] font-bold text-[#3C3C3C] self-start">
              ADMIN DETAILS
            </h2>
      <div className="self-end sm:self-auto">
              {!isEditing && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowChangePasswordModal(true)}
                  className="border border-[#E6E6E6] px-4 py-2 rounded-lg text-[#1E1E1E]"
                >
                  Change password
                </button>
                <button
                  onClick={onEdit}
                  className="bg-[#2283FF] px-4 py-2 text-[#F5F5F5] rounded-lg"
                >
                  Edit profile
                </button>
              </div>
            )}
            {isEditing && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="border border-[#E6E6E6] px-4 py-2 rounded-lg text-[#1E1E1E]"
                >
                  Cancel
                </button>
                <button
                  onClick={onSave}
                  disabled={isSaving}
                  className="bg-[#2283FF] px-4 py-2 text-[#F5F5F5] rounded-lg"
                >
                  {isSaving ? "Saving..." : "Save changes"}
                </button>
              </div>
            )}
      </div>
          </div>

          {/* Error message */}
          {error && <div className="mb-4 text-red-600 text-sm">{error}</div>}

          <div className="flex items-center gap-6 mb-6">
            {/* Admin Profile Picture */}
            <div className="flex-shrink-0">
              <div className="w-24 h-24 relative rounded-full border border-black/10 flex items-center justify-center bg-[#1A212F] text-white">
                {adminProfileImage ? (
                  <img
                    src={adminProfileImage}
                    alt="Admin Profile"
                    className="w-full h-full object-cover rounded-full"
                  />
                ) : (
                  <span className="text-3xl font-bold">{adminInitials}</span>
                )}
                <div className="absolute z-10 bottom-0 right-0 w-7 h-7 rounded-sm flex items-center justify-center">
                  <img
                    src={tp_small}
                    alt="tp_small"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>

            {/* Profile Picture Actions */}
            <div className="flex gap-6 justify-center items-center">
              {isEditing && (
                <button
                  onClick={handleAdminImageUpload}
                  className="text-[#2283FF] px-4 py-2 rounded-lg bg-[#2283FF0D] hover:text-[#2283FF] text-sm font-medium"
                >
                  Replace
                </button>
              )}
            </div>
          </div>

          {/* Admin Details Form */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <CustomInput
              label="Name"
              value={formData.adminName}
              onChange={(e) => handleInputChange("adminName", e.target.value)}
              placeholder="Enter name"
              className="!bg-[#F8F9FB] !text-[#1A212FB2] !text-[14px] !placeholder:text-[14px] !font-medium !placeholder:font-medium !placeholder:text-[#1A212F66]"
              disabled={!isEditing}
            />
          </div>

          {/* Row 2: License Number and Document */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="">
              <CustomInput
                label="License number"
                value={formData.licenseNumber}
                onChange={(e) =>
                  handleInputChange("licenseNumber", e.target.value)
                }
                placeholder="202401-1234"
                className="!bg-[#F8F9FB] !text-[#1A212FB2] !text-[14px] !placeholder:text-[14px] !font-medium !placeholder:font-medium !placeholder:text-[#1A212F66]"
                disabled={!isEditing}
              />
            </div>
          </div>
          <div className="flex flex-col justify-end gap-2 w-fit">
            {agentProfileData?.document_url && (
              <button
                type="button"
                onClick={handleViewLicenseDocument}
                className="flex items-center justify-center gap-2 px-4 py-3 border border-[#2283FF] text-[#2283FF] rounded-lg hover:bg-[#2283FF] hover:text-white transition-colors"
              >
                <FaEye className="w-4 h-4" />
                <span className="text-sm font-medium">View Document</span>
              </button>
            )}
            {isEditing && (
              <button
                type="button"
                onClick={handleUpdateLicense}
                className="flex items-center justify-center gap-2 px-4 py-3 border border-[#2283FF] text-[#2283FF] rounded-lg hover:bg-[#2283FF] hover:text-white transition-colors"
              >
                <FaUpload className="w-4 h-4" />
                <span className="text-sm font-medium">Update License</span>
              </button>
            )}
          </div>
        </div>

        {/* Agency Details Section */}
        <div className="bg-white p-6 border border-[#E6E6E6]">
          <div className="flex items-center gap-3 mb-6">
            <h2 className="text-[11px] font-bold text-[#3C3C3C]">
              AGENCY DETAILS
            </h2>
          </div>

          {/* Show "Join Agency" section when agency_details is empty */}
          {!agentProfileData?.agency_details ||
          Object.keys(agentProfileData.agency_details).length === 0 ? (
            <div className="w-full px-4 py-3 flex items-center justify-between bg-[#FF2D551A] flex-col lg:flex-row gap-2">
              <div>
                <span className="text-[#000000] text-[16px]">
                  You are not a part of any agency. Join an agency to access
                  agency features.
                </span>
              </div>
              <div className="self-end">
                <button
                  onClick={onAgencySearch}
                  className="bg-[#2283FF] px-4 py-2 rounded-lg text-[#F5F5F5] flex gap-2 items-center"
                >
                  <span>Find agency</span>
                  <span>→</span>
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-6 mb-6">
                {/* Agency Logo */}
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 rounded-full border border-black/10 flex items-center justify-center relative bg-[#1A212F] text-white">
                    {agencyLogo ? (
                      <img
                        src={agencyLogo}
                        alt="Agency logo"
                        className="w-full h-full object-cover rounded-full"
                      />
                    ) : (
                      <span className="text-3xl font-bold">{agencyInitials}</span>
                    )}
                    <div className="absolute z-10 bottom-0 right-0 w-7 h-7 rounded-sm flex items-center justify-center">
                      <img
                        src={tp_small}
                        alt="tp_small"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                </div>

                {/* Agency Logo Actions */}
                <div className="flex gap-6 justify-center items-center">
                  {!isAgent && isEditing && (
                    <button
                      onClick={handleAgencyLogoUpload}
                      className="text-[#2283FF] px-4 py-2 rounded-lg bg-[#2283FF0D] hover:text-[#2283FF] text-sm font-medium"
                    >
                      Replace
                    </button>
                  )}
                </div>
              </div>

              {/* Agency Details Form */}
              {/* Row 1: Agency Name */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <CustomInput
                  label="Agency name"
                  value={formData.agencyName}
                  onChange={(e) =>
                    handleInputChange("agencyName", e.target.value)
                  }
                  placeholder="Enter agency name"
                  className="!bg-[#F8F9FB] !text-[#1A212FB2] !text-[14px] !placeholder:text-[14px] !font-medium !placeholder:font-medium !placeholder:text-[#1A212F66]"
                  disabled={!isEditing || isAgent}
                />
              </div>

              {/* Row 2: Email Address */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <CustomInput
                  label="Email address"
                  type="email"
                  value={formData.emailAddress}
                  onChange={(e) =>
                    handleInputChange("emailAddress", e.target.value)
                  }
                  placeholder="Enter email address"
                  icon={<FaEnvelope className="text-gray-400" />}
                  className="!bg-[#F8F9FB] !text-[#1A212FB2] !text-[14px] !placeholder:text-[14px] !font-medium !placeholder:font-medium !placeholder:text-[#1A212F66]"
                  disabled={!isEditing || isAgent}
                />
              </div>

              {/* Row 3: Phone Number */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <PhoneNumberField
                  label="Phone number"
                  value={formData.phoneNumber}
                  placeholder="294 27 84 11 00"
                  disabled
                  showEdit={isEditing}
                  onEdit={() => setShowMobileModal(true)}
                />
              </div>

              {/* Row 4: Agency Website */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <CustomInput
                  label="Agency Website (Optional)"
                  value={formData.agencyWebsite}
                  onChange={(e) =>
                    handleInputChange("agencyWebsite", e.target.value)
                  }
                  placeholder="https://"
                  icon={<FaGlobe className="text-gray-400" />}
                  className="!bg-[#F8F9FB] !text-[#1A212FB2] !text-[14px] !placeholder:text-[14px] !font-medium !placeholder:font-medium !placeholder:text-[#1A212F66]"
                  disabled={!isEditing || isAgent}
                />
              </div>

              {/* Leave Agency Action */}
              {isAgent && (
                <div className="mt-6">
                  <button
                    type="button"
                    onClick={handleOpenLeaveAgency}
                    className="flex items-center px-4 py-2 rounded-lg gap-2 bg-[#2283FF] text-white hover:brightness-105"
                    aria-label="Leave agency"
                  >
                    <span className="text-sm font-medium">Leave agency</span>
                    <FaSignOutAlt className="w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Profile Card Section - Using the reusable component */}
        <ProfileCard disabled={isEditing} />
      </div>

      {/* Admin Profile Image Upload Modal */}
      {showAdminImageUpload && (
        <ImageUploadModal
          onClose={() => setShowAdminImageUpload(false)}
          onUpload={handleAdminImageUploadComplete}
        />
      )}

      {/* Agency Logo Upload Modal */}
      {showAgencyLogoUpload && (
        <ImageUploadModal
          onClose={() => setShowAgencyLogoUpload(false)}
          onUpload={handleAgencyLogoUploadComplete}
        />
      )}

      {/* Find Agency Modal */}
      {showFindAgencyModal && (
        <FindAgencyModal
          onClose={() => setShowFindAgencyModal(false)}
          selectedAgency={selectedAgency}
          onAgencySelect={setSelectedAgency}
        />
      )}

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <ChangePasswordModal
          onClose={() => setShowChangePasswordModal(false)}
          onSuccess={() => {
            // Could add any success handling here
          }}
        />
      )}

      {/* License Update Modal */}
      {showLicenseUpdateModal && (
        <LicenseUpdateModal
          onClose={() => setShowLicenseUpdateModal(false)}
          onSubmit={handleLicenseUpdateSubmit}
          isSubmitting={isUpdatingLicense}
          currentLicenseNumber={formData.licenseNumber}
          hasExistingDocument={Boolean(agentProfileData?.document_url)}
        />
      )}

      {/* Leave Agency Confirm Modal */}
      {showLeaveAgencyModal && (
        <LeaveAgencyConfirmModal
          onCancel={() => setShowLeaveAgencyModal(false)}
          onConfirm={handleConfirmLeaveAgency}
          loading={isLeavingAgency}
        />
      )}

      <MobileNumberUpdateModal
        isOpen={showMobileModal}
        onClose={() => setShowMobileModal(false)}
        initialMobile={formData.phoneNumber || ""}
        onSuccess={handleMobileUpdateSuccess}
      />
    </>
  );
};

// License Update Modal Component
const LicenseUpdateModal = ({
  onClose,
  onSubmit,
  isSubmitting,
  currentLicenseNumber,
  // True when the agent already has a licence document on file — then no
  // re-upload is forced. Defaults to false (conservative: a claimed licence
  // needs a document).
  hasExistingDocument = false,
}) => {
  const [formData, setFormData] = useState({
    license_number: currentLicenseNumber || "",
    license_organization_name: "",
    document: null,
  });
  const [documentFile, setDocumentFile] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (event) => {
    // Shared 10MB + MIME check (src/utils/licenseFile.js) — same rule and
    // same toasts as the agent/agency onboarding steps.
    const file = pickValidLicenseFile(event);
    if (!file) return;

    setFormData((prev) => ({
      ...prev,
      document: file,
    }));
    setDocumentFile(file);
  };

  const handleRemoveFile = () => {
    setFormData((prev) => ({
      ...prev,
      document: null,
    }));
    setDocumentFile(null);
    // Reset the file input
    const fileInput = document.getElementById("license-update-file-upload");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  // Same rule as the onboarding steps: claiming a licence (a number is
  // entered) requires a document — unless one is already on file, in which
  // case the existing document stands and no re-upload is forced.
  const isClaimingLicense = Boolean(
    String(formData.license_number || "").trim()
  );
  const needsDocument =
    isClaimingLicense && !formData.document && !hasExistingDocument;

  const handleSubmit = (e) => {
    e.preventDefault();

    if (needsDocument) {
      toast.error("Please upload your license document");
      return;
    }

    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0A0F2DB2] backdrop-blur">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-[#1A212F] text-[20px] font-bold mb-6">
          Update License
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              License Number
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF]"
              placeholder="202401-1234"
              value={formData.license_number}
              onChange={(e) =>
                handleInputChange("license_number", e.target.value)
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              License Organization Name
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF]"
              placeholder="e.g., FIFA, UEFA"
              value={formData.license_organization_name}
              onChange={(e) =>
                handleInputChange("license_organization_name", e.target.value)
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Upload License Document
            </label>

            {!documentFile ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                <input
                  type="file"
                  id="license-update-file-upload"
                  onChange={handleFileChange}
                  className="hidden"
                  accept={LICENSE_FILE_ACCEPT}
                />
                <label
                  htmlFor="license-update-file-upload"
                  className="cursor-pointer block"
                >
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-gray-700 font-medium">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        PDF, DOC, DOCX, JPG, PNG (Max 10MB)
                      </p>
                    </div>
                  </div>
                </label>
              </div>
            ) : (
              <div className="border border-gray-300 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {documentFile.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(documentFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveFile}
                    className="text-red-500 hover:text-red-700"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {needsDocument && (
              <p className="mt-2 text-sm text-red-600">
                Please upload your license document
              </p>
            )}

            {isClaimingLicense && !formData.document && hasExistingDocument && (
              <p className="mt-2 text-xs text-gray-500">
                Your existing license document stays on file unless you upload
                a new one.
              </p>
            )}
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-[#FFFFFF] border border-[#E6E6E6] px-4 py-2 rounded-lg text-[#1E1E1E] text-[14px]"
              style={{ boxShadow: "0px 1px 5px 0px #00000014" }}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || needsDocument}
              className="flex-1 bg-[#2283FF] px-4 py-2 rounded-lg text-[#F5F5F5] text-[14px] disabled:opacity-50"
              style={{ boxShadow: "0px 1px 5px 0px #00000014" }}
            >
              {isSubmitting ? "Updating..." : "Update License"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Find Agency Modal Component
const FindAgencyModal = ({ onClose, selectedAgency, onAgencySelect }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load agencies function for AsyncDropdown
  const loadAgencies = async (searchTerm) => {
    try {
      const params = {
        type: "agencies",
        query: searchTerm,
        per_page: 10,
      };

      const response = await searchService.search(params);

      if (response.success) {
        // Updated to handle new response structure
        const agencies = response.data.results?.agencies || [];
        return agencies.map((agency) => ({
          value: agency.id,
          label: agency.name,
        }));
      }
      return [];
    } catch (error) {
      console.error("Error loading agencies:", error);
      return [];
    }
  };

  const handleSendRequest = async () => {
    if (!selectedAgency) return;

    setIsSubmitting(true);
    try {
      // TODO: Implement send request API call here
      console.log("Sending request to agency:", selectedAgency);
      // await agentService.sendJoinRequest(selectedAgency);
      onClose();
    } catch (error) {
      console.error("Error sending request:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0A0F2DB2] backdrop-blur">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
        <h2 className="text-[#1A212F] text-[20px] font-bold mb-4">
          Find your agency
        </h2>

        <div className="mb-6">
          <AsyncDropdown
            label="Select Agency"
            placeholder="Search for agencies..."
            value={selectedAgency}
            onChange={onAgencySelect}
            loadOptions={loadAgencies}
            minimumInputLength={2}
            searchPlaceholder="Type agency name..."
            className="text-[#000000] text-[16px] font-medium"
          />
        </div>

        <div className="flex gap-4">
          <button
            onClick={onClose}
            className="flex-1 bg-[#FFFFFF] border border-[#E6E6E6] px-4 py-2 rounded-lg text-[#1E1E1E] text-[14px]"
            style={{ boxShadow: "0px 1px 5px 0px #00000014" }}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSendRequest}
            disabled={!selectedAgency || isSubmitting}
            className="flex-1 bg-[#2283FF] px-4 py-2 rounded-lg text-[#F5F5F5] text-[14px] disabled:opacity-50"
            style={{ boxShadow: "0px 1px 5px 0px #00000014" }}
          >
            {isSubmitting ? "Sending..." : "Send request to join"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgentAccountInfo;

// Reuse-first (My Agency B4 "Submit license"): the agency Agents tab
// reuses this EXACT existing license-submission modal (same fields, same
// updateAgentLicense endpoint, verification stays backend-side) instead
// of duplicating a new one. Named export only — behavior unchanged here.
export { LicenseUpdateModal };

// Leave Agency Confirm Modal (similar styling to StaffPendingInvites reject modal)
const LeaveAgencyConfirmModal = ({ onCancel, onConfirm, loading }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-[24px] w-full max-w-2xl p-10 shadow-2xl">
        <h3 className="text-xl leading-8 font-bold text-[#1A212F] text-center mb-10">
          Are you sure you want to leave this agency?
        </h3>
        <div className="flex items-center justify-center gap-6">
          <button
            onClick={onCancel}
            disabled={loading}
            className="py-2 w-1/2 rounded-lg border border-[#E6E6E6] bg-white text-[#1E1E1E] text-sm shadow-sm disabled:opacity-50"
            style={{ boxShadow: "0px 1px 5px 0px #00000014" }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="bg-[#FF3B30] hover:brightness-105 py-2 w-1/2 rounded-lg text-[#F5F5F5] text-sm disabled:opacity-50"
            style={{ boxShadow: "0px 1px 5px 0px #00000014" }}
          >
            {loading ? "Leaving..." : "Leave"}
          </button>
        </div>
      </div>
    </div>
  );
};
